/**
 * League records.
 *
 * Four views, because the four questions are genuinely different: how has everybody done
 * (careers), who owns the extremes (records), who owns whom (head-to-head), and who has won
 * things (the podium).
 *
 * Every board states what it covers. Yahoo hides manager names before 2009, so a career table
 * that quietly started in 2009 while claiming 23 seasons would be the most plausible-looking
 * wrong number in the app.
 */

import { useMemo, useState, useSyncExternalStore } from 'react';
import {
  GAME_SEASONS, careerTable, draftRecords, finishes, gameRowFor, h2hExtremes, h2hOrder, h2hTotal,
  headToHead, keeperRecords, type H2HSort,
  ordinal, scoringRecords, seasonRecords,
  type Coverage, type Leader, type Leaderboard, type Scope,
} from '../domain/records.js';
import { currentManagers } from '../domain/identity.js';
import { SortableHead, useSort } from './Sortable.js';
import { atLeast, type Role } from '../store/types.js';
import { Games } from './Games.js';
import { AskRecords } from './AskRecords.js';
import { GameDetail } from './GameDetail.js';
import { boxVersion, onBoxesChanged } from '../domain/boxScores.js';
import { GmBadge } from './GmBadge.js';
import { managerShort } from '../domain/identity.js';
import { usePairingVersion, useRosterVersion } from './pairings.js';
import { Linkify } from './RuleText.js';

type View = 'careers' | 'records' | 'h2h' | 'podium' | 'games';

const VIEWS: Array<{ id: View; label: string }> = [
  { id: 'careers', label: 'Careers' },
  { id: 'records', label: 'Records' },
  { id: 'h2h', label: 'Head to head' },
  { id: 'podium', label: 'Podium' },
  { id: 'games', label: 'Games' },
];

/**
 * Which slice of history to argue about.
 *
 * All-time is the honest default and the wrong answer to most questions asked in this league.
 * "Who is the best manager" almost always means "of the people still here", and a table that
 * ranks somebody who left in 2012 above you is not answering it. Neel gets his own option because
 * he is the one departed manager the league still talks about as a peer.
 */
type ScopeId = 'all' | 'decade' | 'current' | 'current-neel';

const RECENT_FROM = Math.max(...GAME_SEASONS) - 9;

const SCOPES: Array<{ id: ScopeId; label: string; note: string }> = [
  { id: 'all', label: 'All-time', note: 'Every season the app can account for.' },
  {
    id: 'decade',
    label: 'Last 10 years',
    note: `${RECENT_FROM}–${Math.max(...GAME_SEASONS)} only — the league as it is actually played now.`,
  },
  {
    id: 'current',
    label: 'Current managers',
    note: 'The ten men holding a team this season, over every year they have played.',
  },
  {
    id: 'current-neel',
    label: 'Current + Neel',
    note: 'The current ten plus Neel, who left in 2017 and is still argued about.',
  },
];

function scopeFor(id: ScopeId): Scope {
  /*
   * `currentManagers()` RATHER THAN A FILTER OF OUR OWN (Tim, 8 September: "current managers
   * filter should not include Neel (Viewer role only, no assigned team this year)"). The rule for
   * who the current ten are is written once, in `identity.ts`, and it turns on holding a SEAT:
   * somebody with a roster entry and no team is a person who signs in to watch. Re-deriving it
   * here from `status` alone quietly readmitted exactly the case that function exists to exclude,
   * and an eleventh row in a ten-man table is the same defect as an eleventh voter on the roll.
   */
  const current = new Set(currentManagers().map((m) => m.id));
  if (id === 'decade') return { season: (y) => y >= RECENT_FROM };
  if (id === 'current') return { gm: (g) => current.has(g) };
  if (id === 'current-neel') return { gm: (g) => current.has(g) || g === 'NEEL' };
  return {};
}

/** The arrow on a sorted column head. Absent, not greyed, on the columns that are not sorted. */
function SortMark({ on, desc }: { on: boolean; desc: boolean }) {
  if (!on) return null;
  return <i className="sortmark" aria-hidden="true">{desc ? '\u2193' : '\u2191'}</i>;
}

export function Records({ role }: { role: Role }) {
  const [view, setView] = useState<View>('careers');
  // Loading a box-score file on the Games tab has to light up the records boards too — the
  // expandable rows there draw the same lineups.
  const boxes = useSyncExternalStore(onBoxesChanged, boxVersion, boxVersion);
  const [scopeId, setScopeId] = useState<ScopeId>('all');
  /*
   * A matchup an Ask the Record answer pointed at. Held here rather than in `Games`, because the
   * click happens on a different tab and the switch and the target have to travel together.
   */
  const [gameFocus, setGameFocus] = useState<{ season: number; week: number; team: string } | null>(null);
  // Every board here is a function of the manager pairings as well as the archives: pairing up
  // a 2007 team name in Settings brings 2007 into all of them, so they recompute on that version
  // rather than once at mount.
  const version = `${usePairingVersion()}.${useRosterVersion()}.${scopeId}`;
  const scope = useMemo(() => scopeFor(scopeId), [version]);
  const careers = useMemo(() => careerTable(scope), [version]);
  const podium = useMemo(() => finishes(scope), [version]);
  const h2h = useMemo(() => headToHead(scope), [version]);

  /*
   * The grid's own sort. Its natural order is the one `headToHead` produces; Manager and Total
   * both reorder BOTH axes so the self-diagonal holds. Clicking the column you are already on
   * flips the direction, which is what everybody expects and nobody is told.
   */
  const [h2hSortKey, setH2hSortKey] = useState<H2HSort | null>(null);
  const [h2hDesc, setH2hDesc] = useState(true);
  const toggleH2H = (key: H2HSort) => {
    if (h2hSortKey === key) return setH2hDesc((d) => !d);
    setH2hSortKey(key);
    setH2hDesc(key === 'total');   // best record first; managers A to Z
  };
  const h2hOrdered = useMemo(
    () => (h2hSortKey ? h2hOrder(h2h.gms, h2h.grid, h2hSortKey, h2hDesc) : h2h.gms),
    [h2h, h2hSortKey, h2hDesc],
  );
  const boards = useMemo(
    () => [
      ...scoringRecords(scope), ...seasonRecords(scope),
      ...keeperRecords(scope), ...draftRecords(scope),
    ],
    [version],
  );
  const scopeNote = SCOPES.find((x) => x.id === scopeId)!;
  // Both tables arrive in a sensible default order — careers by win rate, the podium by titles —
  // and every column is a question somebody asks, so every column sorts.
  const careerSort = useSort<(typeof careers.rows)[number]>('winPct');
  const podiumSort = useSort<(typeof podium.rows)[number]>('firsts');

  return (
    <div className="app wide">
      <Linkify>
        <header className="pagehead">
          <div>
            <h1>Seasons</h1>
            <p className="sub">1,770 matchups across 23 seasons.</p>
          </div>
          <div className="panes small">
            {VIEWS.map((v) => (
              <button key={v.id} type="button" className={`pane ${view === v.id ? 'on' : ''}`}
                onClick={() => setView(v.id)}>{v.label}</button>
            ))}
          </div>
        </header>

        <div className="scopebar">
          <div className="panes small">
            {SCOPES.map((sc) => (
              <button key={sc.id} type="button" className={`pane ${scopeId === sc.id ? 'on' : ''}`}
                onClick={() => setScopeId(sc.id)}>{sc.label}</button>
            ))}
          </div>
          <span className="mut">{scopeNote.note}</span>
        </div>

        {view === 'careers' && (
          <section className="card">
            <div className="striptop">
              <h3 className="striphead">All-time</h3>
              <CoverageNote c={careers.coverage} />
            </div>
            <table className="records">
              <thead>
                <tr>
                  <SortableHead sort={careerSort} col="gm" label="GM" text />
                  <SortableHead sort={careerSort} col="seasons" label="Seasons" />
                  <SortableHead sort={careerSort} col="w" label="W" />
                  <SortableHead sort={careerSort} col="l" label="L" />
                  <SortableHead sort={careerSort} col="winPct" label="Win %" />
                  <SortableHead sort={careerSort} col="pf" label="PF" />
                  <SortableHead sort={careerSort} col="ppg" label="PPG" />
                  <SortableHead sort={careerSort} col="playoffs" label="Playoffs" />
                  <SortableHead sort={careerSort} col="bestFinish" label="Best" asc />
                  <SortableHead sort={careerSort} col="championships" label="Titles" />
                </tr>
              </thead>
              <tbody>
                {careerSort.rows(careers.rows).map((r) => (
                  <tr key={r.gm}>
                    <td><GmBadge gm={r.gm} withName /></td>
                    <td className="num">{r.seasons}</td>
                    <td className="num">{r.w}</td>
                    <td className="num">{r.l}</td>
                    <td className="num strong">{(r.winPct * 100).toFixed(1)}%</td>
                    <td className="num mut">{r.pf.toLocaleString()}</td>
                    <td className="num">{r.ppg.toFixed(1)}</td>
                    <td className="num mut">{r.playoffs}</td>
                    <td className="num mut">{ordinal(r.bestFinish)}</td>
                    <td>
                      {Array.from({ length: r.championships }, (_, i) => (
                        <span key={i} className="trophy" title="Champion">★</span>
                      ))}
                      {Array.from({ length: r.runnerUps }, (_, i) => (
                        <span key={`r${i}`} className="trophy second" title="Runner-up">☆</span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {view === 'records' && (
          <div className="cardrow two">
            {boards.map((b) => <Board key={b.title} board={b} boxes={boxes} />)}
          </div>
        )}

        {/*
          OPEN TO THE LEAGUE — Tim's call, 2026-08-18.
          It shipped behind a commissioner flag because it cost money per question and nobody had
          seen whether the answers were any good. Both have been answered: the obvious misses never
          reach the model at all, the canned questions run with no model whatsoever, and a wrong
          answer is now the failure it was designed to be — a visibly wrong query rather than a
          plausible wrong number. A records feature only ten per cent of the league can reach is
          not a records feature.

          It stays out of the VIEWER's hands, not for cost but for consistency: a viewer holds no
          team and takes no action, and asking the app to spend money on his behalf is an action.
        */}
        {atLeast(role, 'manager') && (
          <AskRecords onOpenGame={(g) => { setGameFocus(g); setView('games'); }} />
        )}

        {view === 'h2h' && (
          <section className="card">
            <div className="striptop">
              <h3 className="striphead">Head to head</h3>
              <CoverageNote c={h2h.coverage} />
            </div>
            <p className="note">
              Read across: the row is the GM, the column is the opponent. Green is a winning
              record against that man, red a losing one, and the percentage counts a tie as half
              a win — the same way the league's own standings do. A ringed cell is that GM&rsquo;s
              best or worst matchup among the managers the filter is showing. Hold or hover a cell
              for the points.
            </p>
            <div className="gridwrap">
              <table className="h2h">
                <thead>
                  <tr>
                    {/*
                      Manager and Total both sort, and both reorder the COLUMNS as well as the
                      rows — one order for both axes, or the self-diagonal wanders across the grid
                      and the matrix stops being readable however right each cell is.
                    */}
                    <th className="sortable" aria-sort={h2hSortKey === 'manager' ? (h2hDesc ? 'descending' : 'ascending') : 'none'}>
                      <button type="button" onClick={() => toggleH2H('manager')}>
                        Manager<SortMark on={h2hSortKey === 'manager'} desc={h2hDesc} />
                      </button>
                    </th>
                    {h2hOrdered.map((g) => <th key={g}><GmBadge gm={g} /></th>)}
                    <th className="num sortable" aria-sort={h2hSortKey === 'total' ? (h2hDesc ? 'descending' : 'ascending') : 'none'}>
                      <button type="button" onClick={() => toggleH2H('total')}>
                        Total<SortMark on={h2hSortKey === 'total'} desc={h2hDesc} />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {h2hOrdered.map((a) => {
                    const row = h2h.grid.get(a);
                    const total = h2hTotal(row, h2hOrdered, a);
                    const { best, worst } = h2hExtremes(row, h2hOrdered, a);
                    return (
                      <tr key={a}>
                        <th><GmBadge gm={a} withName /></th>
                        {h2hOrdered.map((b) => {
                          const c = row?.get(b);
                          if (a === b || !c) return <td key={b} className="self" />;
                          const tone = c.w > c.l ? 'up' : c.w < c.l ? 'down' : 'even';
                          const edge = best.has(b) ? ' best' : worst.has(b) ? ' worst' : '';
                          const why = best.has(b) ? ' — his best matchup here'
                            : worst.has(b) ? ' — his worst matchup here' : '';
                          return (
                            <td key={b} className={`h2hcell ${tone}${edge}`}
                              title={`${managerShort(a)} ${c.w}-${c.l}${c.t ? `-${c.t}` : ''} vs ${managerShort(b)} over ${c.games} meetings · ${c.pf.toFixed(0)}–${c.pa.toFixed(0)} points${why}`}>
                              <span className="rec">{c.w}-{c.l}{c.t ? `-${c.t}` : ''}</span>
                              <span className="pct">{(c.pct * 100).toFixed(0)}%</span>
                            </td>
                          );
                        })}
                        <td className="num strong">
                          <span className="rec">{total.w}-{total.l}{total.t ? `-${total.t}` : ''}</span>
                          <span className="pct">{(total.pct * 100).toFixed(0)}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {view === 'games' && (
          <Games scope={scope} scopeLabel={scopeNote.label} role={role} focus={gameFocus} />
        )}

        {view === 'podium' && (
          <section className="card">
            <div className="striptop">
              <h3 className="striphead">Where everybody has finished</h3>
              <CoverageNote c={podium.coverage} />
            </div>
            <table className="records">
              <thead>
                <tr>
                  <SortableHead sort={podiumSort} col="gm" label="GM" text />
                  <SortableHead sort={podiumSort} col="firsts" label="Titles" />
                  <SortableHead sort={podiumSort} col="seconds" label="2nd" />
                  <SortableHead sort={podiumSort} col="thirds" label="3rd" />
                  <SortableHead sort={podiumSort} col="toilets" label="Toilet Bowl" />
                  <SortableHead sort={podiumSort} col="lasts" label="Last" />
                </tr>
              </thead>
              <tbody>
                {podiumSort.rows(podium.rows).map((r) => (
                  <tr key={r.gm}>
                    <td><GmBadge gm={r.gm} withName /></td>
                    <td className="num strong">{r.firsts || ''}</td>
                    <td className="num">{r.seconds || ''}</td>
                    <td className="num mut">{r.thirds || ''}</td>
                    <td className="num mut">{r.toilets || ''}</td>
                    <td className="num mut">{r.lasts || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="note">
              The Toilet Bowl column counts consolation-bracket wins, which Yahoo ranks
              immediately below the playoff field — the right that 2020-II-1 §3 turns into the
              second choice of draft slot.
            </p>
          </section>
        )}
      </Linkify>
    </div>
  );
}

/**
 * A leaderboard.
 *
 * Where an entry IS a game — the narrowest win, the biggest blowout — it opens on click to the
 * game behind it, the same lineups the Games list shows. Nobody believes a 0.12-point margin
 * until they can see which two lineups produced it, and the next question is always who did that
 * and what was sitting on the bench.
 *
 * Season and keeper records carry no single game and stay inert: they render as plain rows, not
 * as buttons that do nothing, because a row that looks clickable and is not is worse than one
 * that never invited the click.
 */
function Board({ board, boxes }: { board: Leaderboard; boxes: number }) {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section className="card">
      <div className="striptop">
        <h3 className="striphead">{board.title}</h3>
        <CoverageNote c={board.coverage} />
      </div>
      {board.note && <p className="note">{board.note}</p>}
      <ol className="leaderboard">
        {board.entries.map((e, i) => (
          <Entry
            key={`${e.label}${e.gm}${i}`}
            e={e}
            rank={i + 1}
            boxes={boxes}
            open={open === i}
            onToggle={() => setOpen(open === i ? null : i)}
          />
        ))}
      </ol>
    </section>
  );
}

function Entry(
  { e, rank, boxes, open, onToggle }:
  { e: Leader; rank: number; boxes: number; open: boolean; onToggle: () => void },
) {
  const row = useMemo(() => (e.game ? gameRowFor(e.game) : null), [e.game, boxes]);
  const inner = (
    <>
      <span className="rank">{rank}</span>
      <span className="val">{e.value}</span>
      <span className="who">
        {/^[A-Z]+$/.test(e.gm) ? <GmBadge gm={e.gm} withName /> : <b>{e.gm}</b>}
      </span>
      <span className="mut when">{e.label}</span>
      <span className="mut detail">{e.detail}</span>
    </>
  );

  if (!row) return <li>{inner}</li>;

  return (
    <li className={`opens ${open ? 'on' : ''}`}>
      <button type="button" className="leadhead" onClick={onToggle} aria-expanded={open}>
        {inner}
        <span className="chev" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && <GameDetail g={row} boxes={boxes} />}
    </li>
  );
}

function CoverageNote({ c }: { c: Coverage }) {
  const gaps = c.excluded.length;
  return (
    <span className="mut coverage" title={c.excluded.map((e) => `${e.season}: ${e.why}`).join('\n')}>
      {c.from}–{c.to}
      {gaps > 0 && <> · {gaps} season{gaps === 1 ? '' : 's'} not counted</>}
    </span>
  );
}
